import React, { useCallback, useImperativeHandle, useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import CardTemplate from "components/card/card-template/CardTemplate";
import { YesNoRadioField, SecondaryAddresseeFields } from "components/form-fields";
import type { SecondaryAddresseeFormReturn, SecondaryAddresseeRef, DTOApplicationFormReturn } from "types/Card";
import { toUsStates } from "constants/validations";
import { useContactInformation } from "hooks/useContactInformation";
import InfoSecondaryAddresseeModal from "components/modal/info-secondary-addressee/InfoSecondaryAddresseeModal";
import { useProductContext } from "context/ProductContext";

interface SecondaryAddresseeProps {
    className?: string;
    form: SecondaryAddresseeFormReturn;
}

type SecondaryAddresseeWithRef = SecondaryAddresseeProps & {
    ref?: React.Ref<SecondaryAddresseeRef>;
};

const SecondaryAddressee = ({ ref, ...props }: SecondaryAddresseeWithRef) => {
    const { className = "", form } = props;
    // ========== Hooks ==========
    const { isGIWL } = useProductContext();
    const { contactData } = useContactInformation();

    const typedForm = form as DTOApplicationFormReturn;
    const {
        register,
        formState: { errors },
        watch,
        setValue,
        trigger,
        getValues,
    } = typedForm;

    // ========== State ==========
    const secondaryAddresseeDataRedux = contactData?.secondaryAddressee;
    const hasRestoredRef = useRef(false);

    const hasSecondaryAddressee = watch("secondaryAddressee.hasSecondaryAddressee");
    const secondaryAddressee = useWatch({
        control: typedForm.control as any,
        name: "secondaryAddressee",
    });

    // ========== Effects ==========
    // Restore form data from Redux once, the first time saved data is available
    // (mount / back-navigation). The autosave persists this card's values on every
    // card blur, so re-running on each contactData change would push the persisted
    // hasSecondaryAddressee (null until the user picks) back into the form and clobber
    // an in-progress selection.
    useEffect(() => {
        if (hasRestoredRef.current || !secondaryAddresseeDataRedux) return;
        hasRestoredRef.current = true;

        setValue("secondaryAddressee.hasSecondaryAddressee", secondaryAddresseeDataRedux.hasSecondaryAddressee ?? null);

        if (secondaryAddresseeDataRedux.hasSecondaryAddressee) {
            setValue("secondaryAddressee.firstName", secondaryAddresseeDataRedux.firstName || "");
            setValue("secondaryAddressee.lastName", secondaryAddresseeDataRedux.lastName || "");
            setValue("secondaryAddressee.address", secondaryAddresseeDataRedux.address || "");
            setValue("secondaryAddressee.city", secondaryAddresseeDataRedux.city || "");
            setValue("secondaryAddressee.state", secondaryAddresseeDataRedux.state || "");
            setValue("secondaryAddressee.zipCode", secondaryAddresseeDataRedux.zipCode || "");
            setValue("secondaryAddressee.phone", secondaryAddresseeDataRedux.phone || "");
        }
    }, [secondaryAddresseeDataRedux, setValue]);

    // ========== Event Handlers ==========
    const handleRadioChange = useCallback(
        async (value: boolean) => {
            setValue("secondaryAddressee.hasSecondaryAddressee", value);
            await trigger("secondaryAddressee.hasSecondaryAddressee");
        },
        [setValue, trigger]
    );

    // ========== Ref Methods ==========
    const validateForm = useCallback(async () => {
        const formData = getValues("secondaryAddressee");
        // Skip validation of fields if user selected "No"
        if (formData.hasSecondaryAddressee === false) {
            return trigger("secondaryAddressee.hasSecondaryAddressee");
        }
        // Validate all fields if "Yes" is selected
        return trigger("secondaryAddressee");
    }, [getValues, trigger]);

    const getFormValues = useCallback(() => {
        const formData = getValues("secondaryAddressee");
        // If user selected "No", clear all fields except the selection
        if (formData.hasSecondaryAddressee === false) {
            return {
                hasSecondaryAddressee: false,
                firstName: undefined,
                lastName: undefined,
                address: undefined,
                city: undefined,
                state: undefined,
                zipCode: undefined,
                phone: undefined,
            };
        }
        // Return actual values if "Yes" is selected
        return {
            hasSecondaryAddressee: formData.hasSecondaryAddressee,
            firstName: formData.firstName,
            lastName: formData.lastName,
            address: formData.address,
            city: formData.city,
            state: toUsStates(formData.state)!,
            zipCode: formData.zipCode,
            phone: formData.phone,
        };
    }, [getValues]);

    useImperativeHandle(
        ref,
        () => ({
            validate: validateForm,
            getValues: getFormValues,
        }),
        [validateForm, getFormValues]
    );

    // ========== Render ==========
    return (
        <CardTemplate
            title={
                !isGIWL ? (
                    <h2 className="text-xl font-semibold text-base-content text-center p-2">Secondary Addressee</h2>
                ) : (
                    <div className="flex items-center gap-2 p-2">
                        <h2 className="text-xl font-semibold text-base-content text-center">Secondary Addressee</h2>
                        <InfoSecondaryAddresseeModal />
                    </div>
                )
            }
            className={className}
        >
            <form className="flex flex-col" data-testid="secondary-addressee-form" onSubmit={(e) => e.preventDefault()}>
                <p className="text-sm font-medium leading-5 text-base-content pb-5">
                    AAA Life Insurance Company provides you with the option to designate someone, in addition to yourself, to
                    receive notice when your life insurance premium is outstanding. This is an ideal way to ensure that the
                    valuable coverage you have remains in effect. This is optional and not required as part of the application
                    process.
                </p>

                <p className="text-sm font-semibold leading-5 text-base-content pb-3">
                    Do you wish to designate a secondary addressee?
                </p>

                <div className="flex flex-col pb-3">
                    <YesNoRadioField
                        name="hasSecondaryAddressee"
                        value={hasSecondaryAddressee}
                        onChange={handleRadioChange}
                        error={errors.secondaryAddressee?.hasSecondaryAddressee?.message}
                        yesId="secondary-addressee-has-secondary-addressee-yes"
                        noId="secondary-addressee-has-secondary-addressee-no"
                    />
                </div>

                {hasSecondaryAddressee && (
                    <SecondaryAddresseeFields
                        register={register}
                        errors={errors}
                        state={secondaryAddressee?.state}
                        phone={secondaryAddressee?.phone}
                    />
                )}
            </form>
        </CardTemplate>
    );
};

SecondaryAddressee.displayName = "SecondaryAddressee";

export default SecondaryAddressee;
